import {
  getRequiredMessage,
  IBaseResponse,
  UseEntityDataResponseType,
} from '@monetize/types/common';
import {
  getCurrencyDecimals,
  roundNumberToDecimal,
} from '@monetize/utils/core';
import { z } from 'zod';
import { IAccountRespSchema } from './account.types';
import { AddressSchema } from './address.types';
import { ContactRespSchema, IContactRespSchema } from './contact.types';
import { IPaymentGateway } from './paymentGateway.types';

export const PaymentMethodContactSchema = z
  .object({
    fullName: z.string(),
    email: z.string().nullish(),
    phone: z.string().nullish(),
    title: z.string().nullish(),
  })
  .extend(AddressSchema.omit({ id: true, description: true }).shape);

export type IPaymentMethodContactSchema = z.infer<
  typeof PaymentMethodContactSchema
>;

/**
 * Input schema - what the form accepts (before transformation)
 */
export const MakePaymentFormInputSchema = z.object({
  type: z.enum(['manual', 'gateway']),
  paymentMethodId: z.string().nullish(),
  /** Drives the amount's precision, and is sent with manual payments. */
  currency: z.string(),
  amount: z
    .union([z.string(), z.number()])
    .nullish()
    .refine((val) => val !== null && val !== undefined && val !== '', {
      message: getRequiredMessage('Amount'),
    }),
  paymentDate: z.string(),
  referenceNo: z.string().nullish(),
  description: z.string().max(255).nullish(),
});

/**
 * Output schema - what the form produces (after transformation)
 */
export const MakePaymentFormOutputSchema = z.object({
  type: z.enum(['manual', 'gateway']),
  paymentMethodId: z.string().nullish(),
  currency: z.string(),
  amount: z.number(),
  paymentDate: z.string(),
  referenceNo: z.string().nullish(),
  description: z.string().max(255).nullish(),
});

/**
 * Full schema with transformation and validation
 */
export const MakePaymentFormSchema = MakePaymentFormInputSchema.transform(
  (data) => ({
    ...data,
    amount: roundNumberToDecimal(
      Number(data.amount),
      getCurrencyDecimals(data.currency),
    ),
  }),
).pipe(
  MakePaymentFormOutputSchema.superRefine((data, ctx) => {
    if (data.type === 'gateway') {
      if (!data.paymentMethodId) {
        ctx.addIssue({
          code: 'custom',
          message: `Payment method is required`,
          path: ['paymentMethodId'],
        });
      }
    }
  }),
);

export type MakePaymentFormInput = z.infer<typeof MakePaymentFormInputSchema>;
export type MakePaymentForm = z.infer<typeof MakePaymentFormOutputSchema>;

export const ManualPaymentUpdateReqSchema = MakePaymentFormInputSchema.pick({
  paymentDate: true,
  referenceNo: true,
  description: true,
});

export type ManualPaymentUpdateReq = z.infer<
  typeof ManualPaymentUpdateReqSchema
>;

export const GatewayPaymentSchema = z.object({
  id: z.string().nonempty('Required'),
  amount: z.number().nonnegative('Must be a positive number'),
});
export type IGatewayPayment = z.infer<typeof GatewayPaymentSchema>;

export const ManualPaymentSchema = z.object({
  amount: z
    .union([z.number(), z.string()])
    .nullish()
    .refine((val) => val !== null && val !== undefined && val !== '', {
      message: getRequiredMessage('Amount'),
    })
    .transform((val) => roundNumberToDecimal(Number(val), 2)),
  paymentDate: z.string().nonempty(getRequiredMessage('Payment Date')),
  referenceNo: z.string().nullish(),
  description: z.string().max(255).nullish(),
  /** Account payments only - the backend falls back to the account default when omitted. */
  currency: z.string().nullish(),
});
export type IManualPayment = z.infer<typeof ManualPaymentSchema>;

// from POST /api/accounts/{accountId}/paymentMethods
export const PaymentMethodReqSchema = z.object({
  billingDetails: PaymentMethodContactSchema,
  paymentMethodName: z.string().nullish(),
  paymentToken: z.string(),
  paymentGatewayId: z.string(),
  customId: z.string().nullish(),
  description: z.string().nullish(), // FE no longer using this
});
export type IPaymentMethodReq = z.infer<typeof PaymentMethodReqSchema>;

export const ImportPaymentMethodReqSchema = z.object({
  gatewayAccountId: z
    .string()
    .nonempty(getRequiredMessage('Payment Account Id')),
  gatewayId: z.string().nonempty(getRequiredMessage('Gateway Account Id')),
});
export type IImportPaymentMethodReqSchema = z.infer<
  typeof ImportPaymentMethodReqSchema
>;

export const PaymentMethodCancelSchema = z.object({
  cancellationReason: z
    .string()
    .max(255, 'Should not be more than 255 characters')
    .nullish(),
});

export type PaymentMethodCancelReq = z.infer<typeof PaymentMethodCancelSchema>;

export type PaymentMethodCancelUI = PaymentMethodCancelReq;

// represents the only 2 fields the user submits via the UI, other fields come from Stripe or are unused
export const PaymentMethodReqSchemaUI = PaymentMethodReqSchema.pick({
  paymentMethodName: true,
}).extend({
  /** contactId is used to handle the UI selection, but then all data gets populated on billingDetails */
  contactId: z.string().nonempty(getRequiredMessage('Contact')),
  /** This is set from the contactId, and this turns into billingDetails on actual request */
  contact: ContactRespSchema.nullish(),
});
export type IPaymentMethodReqUI = z.infer<typeof PaymentMethodReqSchemaUI>;

export enum PaymentMethodStatusEnum {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  CANCELED = 'CANCELED',
  EXPIRED = 'EXPIRED',
}
export const PaymentMethodStatusEnumZ = z.enum(PaymentMethodStatusEnum);

export enum PaymentMethodTypeEnum {
  CREDIT_CARD = 'CREDIT_CARD',
  ACH_CREDIT = 'ACH_CREDIT',
  US_BANK_TRANSFER = 'US_BANK_TRANSFER',
  DIRECT_DEBIT = 'DIRECT_DEBIT',
  MANUAL = 'MANUAL',
}
export const PaymentMethodTypeEnumZ = z.enum(PaymentMethodTypeEnum);

export enum CreditCardTypeEnum {
  VISA = 'VISA',
  MASTERCARD = 'MASTERCARD',
  DISCOVER = 'DISCOVER',
  AMEX = 'AMEX',
  DINERS = 'DINERS',
  JCB = 'JCB',
  UNIONPAY = 'UNIONPAY',
}
export const CreditCardTypeEnumZ = z.enum(CreditCardTypeEnum);

export type IPaymentMethodResp = IBaseResponse & {
  paymentMethodName: string;
  paymentMethodType: PaymentMethodTypeEnum;
  paymentGateway: IPaymentGateway;
  account?: IAccountRespSchema;
  addressValidation?: any;
  gatewayCustomerId: string;
  /** @deprecated - use billingDetails */
  contact?: IContactRespSchema;
  billingDetails: IPaymentMethodContactSchema;
  creditCardType: CreditCardTypeEnum;
  creditCardGateWayToken?: any;
  creditCardNumber?: any;
  cvcValidation?: boolean;
  expirationMonth?: number;
  expirationYear?: number;
  customId?: string;
  defaultPaymentMethod?: boolean;
  description?: string;
  externalPaymentMethodId?: string;
  lastFour?: string;
  postalValidation?: any;
  status?: PaymentMethodStatusEnum;
  userId?: string;
  amount?: string;
  date?: string;
  referenceNo?: string;
  comment?: string;
  accountType?: string; // Direct Debit
  bankName?: string; // Direct Debit, ACH Credit
  routingNumber?: string; // Direct Debit, ACH Credit
  swiftCode?: string; // ACH Credit
  accountNumber?: string; // ACH Credit
  cancellationReason?: string; // Canceled payment method
  cancellationDate?: string; // Canceled payment method
};

export const PaymentMethodSummaryResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  isDefault: z.boolean(),
  status: PaymentMethodStatusEnumZ,
  paymentMethodType: PaymentMethodTypeEnumZ,
  description: z.string().nullish(),
  lastFour: z.string().nullish(),
  creditCardType: z.string().nullish(),
  expirationMonth: z.number().nullish(),
  expirationYear: z.number().nullish(),
  bankName: z.string().nullish(),
});

export type PaymentMethodSummaryResponse = z.infer<
  typeof PaymentMethodSummaryResponseSchema
>;

export type PaymentMethodsDataType = Omit<
  UseEntityDataResponseType<IPaymentMethodResp>,
  'loadData'
> & {
  selected: any;
  setSelected: any;
  openCreateModal: any;
  openEditModal: any;
};
export type ImportablePaymentMethodItem = {
  type: PaymentMethodTypeEnum;
  id: null | string;
  externalId: string;
  lastFour: string;

  routingNumber?: string;
  bankName?: string;
  accountType?: string;
  accountHolderType?: string;

  cardType?: string;
  expirationYear?: number;
  expirationMonth?: number;
  cvcValidation?: boolean;
  addressValidation?: boolean;
  postalValidation?: boolean;

  swiftCode?: string;
  accountNumber?: string;
};
export type ImportablePaymentMethodResp = {
  gatewayAccount: {
    id: string;
    name: string;
    email: string;
  };
  paymentMethods: ImportablePaymentMethodItem[];
};
export type ImportPaymentMethodResp = {
  gatewayAccount: {
    id: string;
    name: string;
    email: string;
  };
  succeededImports: ImportablePaymentMethodItem[];
  failedImports: {
    externalPaymentMethodId: string;
    reason: string;
  }[];
  existingPaymentMethods: ImportablePaymentMethodItem[];
};

export enum ImportStep {
  SELECT_GATEWAY_ACCOUNT_ID = 1,
  SELECT_PAYMENT_METHOD = 2,
  SUBMITTED_PAYMENT_METHOD = 3,
}

export const WireTransferPaymentMethodCreateSchema = z.object({
  contactId: z.string(),
});

export type WireTransferPaymentMethodCreateReq = z.infer<
  typeof WireTransferPaymentMethodCreateSchema
>;

export type WireTransferPaymentMethodCreateUI =
  WireTransferPaymentMethodCreateReq;

export const LinkPaymentGatewayAccountSchema = z.object({
  gatewayAccountId: z
    .string()
    .nonempty(getRequiredMessage('Payment Account Id')),
});

export type LinkPaymentGatewayAccountReq = z.infer<
  typeof LinkPaymentGatewayAccountSchema
>;

export type LinkPaymentGatewayAccountUI = LinkPaymentGatewayAccountReq;
